import { debounce, isEqual, throttle } from 'lodash'
import { MapBrowserEvent } from 'ol'
import { Point } from 'ol/geom'
import React, {
  ReactElement,
  useCallback,
  useEffect,
  useRef,
  useState,
} from 'react'
import { useRecoilState } from 'recoil'

import { useMap } from '../useMap'
import { getMapPixelFromMouseEvent } from '../utils/getMapPixelFromMouseEvent'
import { hoveredItemsState } from './state'
import { HoveredItem } from './types'

type HoveredItemsProps = {
  debounceDelay?: number
}

export const HoveredItems = (props: HoveredItemsProps): ReactElement => {
  const { debounceDelay = 5 } = props

  const { map } = useMap()

  const [hoveredItemsRecoil, setHoveredItemsRecoil] =
    useRecoilState(hoveredItemsState)

  const cachedHoveredItems = useRef<HoveredItem[]>([])
  const [hoveredItems, setHoveredItems] = useState<HoveredItem[]>([])

  useEffect(() => {
    setHoveredItemsRecoil(hoveredItems)
  }, [hoveredItems])

  const onMoveEnd = useCallback(() => {
    if (cachedHoveredItems.current.length > 0) {
      setHoveredItems(cachedHoveredItems.current)
      cachedHoveredItems.current = []
    }
  }, [map])

  const onPointerMove = useCallback(
    debounce(
      (event: Pick<MapBrowserEvent<PointerEvent>, 'pixel' | 'dragging'>) => {
        if (!map) {
          return
        }
        if (event.dragging) {
          if (hoveredItems.length > 0) {
            cachedHoveredItems.current = hoveredItems
            setHoveredItems([])
          }
          return
        }

        const items: HoveredItem[] = []
        map.forEachFeatureAtPixel(
          event.pixel,
          (feature, layer) => {
            items.push({
              featureId: String(feature.getId()),
              layerName: layer.getClassName(),
              pixel: map.getCoordinateFromPixel(event.pixel),
              position:
                feature.getGeometry() instanceof Point
                  ? (feature.getGeometry() as Point).getCoordinates()
                  : undefined,
            })
          },
          {},
        )

        const getComparatorItem = (item: HoveredItem): string => {
          return item.featureId + item.layerName
        }
        if (
          isEqual(
            items.map(getComparatorItem),
            hoveredItems.map(getComparatorItem),
          )
        ) {
          return
        }

        map.getTargetElement().style.cursor =
          items.length > 0 ? 'pointer' : 'default'

        setHoveredItems(items)
      },
      debounceDelay,
      {
        maxWait: 100000,
      },
    ),
    [map, hoveredItems],
  )

  useEffect(() => {
    if (!map) {
      return
    }

    map.on('pointermove', onPointerMove)

    return () => {
      map.un('pointermove', onPointerMove)
    }
  }, [map, onPointerMove])

  useEffect(() => {
    if (!map) {
      return
    }

    map.on('moveend', onMoveEnd)
    return () => {
      map.un('moveend', onMoveEnd)
    }
  }, [map, onMoveEnd])

  const onDrag = useCallback(
    throttle((event: DragEvent) => {
      const pixel = getMapPixelFromMouseEvent(map, event)
      onPointerMove({
        pixel,
        dragging: false,
      })

      // map.getTargetElement()
    }, 50),
    [map, onPointerMove],
  )

  // dragging
  useEffect(() => {
    document.addEventListener('drag', onDrag)

    return () => {
      document.removeEventListener('drag', onDrag)
    }
  }, [onDrag])

  return <></>
}
